import React, { useMemo, useState, useEffect } from 'react';
import { Box, Heading, ButtonLink, Spinner } from '@indeed/ifl-components';
import {
    Edit,
    OpenDoor,
    MoneyStack,
    Commute,
    Location,
    Home,
    Profile,
    Jobs,
    Clock,
} from '@indeed/ifl-icons';
import { useLocation } from 'react-router-dom';
import { JobPreferenceField } from '../job-preferences/JobPreferenceField';
import { useInitialState } from '../../../lib/context/InitialStateContext';
import { ErrorDialog } from '../../../components/ErrorDialog/ErrorDialog';
import GettextFactory from '../../../util/GettextFactory';
import {
    useGetMatchPreferencesQuery,
    GetMatchPreferencesQuery,
} from './__generated__/MatchPreferencesSection.generated';
import {
    JobSeekerProfileStructuredDataSalaryType,
    TaxonomyConceptsInput,
} from '../../../__generated__/graphql-base-types';
import { REMOTE_WORK_TYPE_CUSTOM_CLASS } from '../constants';
import { useEventLogger } from '../../../lib/eventLogger/EventLoggerProvider';
import { AutoApplySetupRumContext, logAutoApplySetupRumError } from '../autoApplySetupRumErrors';

// Type aliases extracted from generated GraphQL types
type Preference = NonNullable<
    GetMatchPreferencesQuery['jobSeekerProfileStructuredData']
>['preferences'][number];
type JobTitle = Preference['jobTitles'][number];
type WorkArea = Preference['workAreas'][number];
type JobType = Preference['jobTypes'][number];
type Shift = Preference['shifts'][number];
type LocationData = Preference['locations'][number];

/** Formats minimum pay as currency string with salary type period */
const formatMinimumPay = (
    amount: number | null | undefined,
    currency: string | null | undefined,
    salaryType: JobSeekerProfileStructuredDataSalaryType | null | undefined,
    locale: string,
    gt: ReturnType<typeof GettextFactory>,
): string | null => {
    if (!amount) return null;
    const formattedAmount = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency || 'USD',
        maximumFractionDigits: 0,
    }).format(amount);
    switch (salaryType) {
        case 'HOURLY':
            return gt.pgettext('salary expectation', '{0} per hour', String(formattedAmount));
        case 'DAILY':
            return gt.pgettext('salary expectation', '{0} per day', String(formattedAmount));
        case 'WEEKLY':
            return gt.pgettext('salary expectation', '{0} per week', String(formattedAmount));
        case 'BIWEEKLY':
            return gt.pgettext('salary expectation', '{0} per 2 weeks', String(formattedAmount));
        case 'MONTHLY':
            return gt.pgettext('salary expectation', '{0} per month', String(formattedAmount));
        case 'YEARLY':
            return gt.pgettext('salary expectation', '{0} per year', String(formattedAmount));
        default:
            return gt.pgettext('salary expectation', '{0}', String(formattedAmount));
    }
};

// Helper to join non-null strings with commas
const joinLabels = (labels: (string | null | undefined)[]): string | null => {
    const filtered = labels.filter((label): label is string => !!label);
    return filtered.length > 0 ? filtered.join(', ') : null;
};

// Helper to filter items with POSITIVE sentiment and extract labels
const getPositiveLabels = <T extends { sentiment: string }>(
    items: T[] | null | undefined,
    getLabel: (item: T) => string | null | undefined,
): string | null => {
    if (!items?.length) return null;
    return joinLabels(items.filter((item) => item.sentiment === 'POSITIVE').map(getLabel));
};

/** Formats job titles as comma-separated string */
const formatJobTitles = (jobTitles: JobTitle[] | null | undefined): string | null =>
    joinLabels(jobTitles?.map((jt) => jt.jobTitle) ?? []);

/** Formats work areas (hidden if empty - profile redesign feature) */
const formatWorkAreas = (workAreas: WorkArea[] | null | undefined): string | null =>
    getPositiveLabels(workAreas, (wa) => wa.workAreaTaxonomyConcept?.label);

/** Formats job types with taxonomy label fallback */
const formatJobTypes = (jobTypes: JobType[] | null | undefined): string | null =>
    getPositiveLabels(jobTypes, (jt) => jt.jobTypeTaxonomyConcept?.label || jt.jobType);

/** Formats work schedules/shifts with taxonomy label fallback */
const formatShifts = (shifts: Shift[] | null | undefined): string | null =>
    getPositiveLabels(shifts, (s) => s.shiftTaxonomyConcept?.label || s.shift);

/** Formats commute time (hidden if empty - profile redesign feature) */
const formatCommute = (
    timeMinutes: number | null | undefined,
    gt: ReturnType<typeof GettextFactory>,
): string | null => (timeMinutes ? gt.gettext('Up to {0} mins', String(timeMinutes)) : null);

/** Formats relocation preference with optional destination locations */
const formatRelocation = (
    ableToRelocate: boolean | null | undefined,
    locations: LocationData[] | null | undefined,
    gt: ReturnType<typeof GettextFactory>,
): string | null => {
    if (ableToRelocate == null) return null;
    if (!ableToRelocate) return gt.gettext('Not willing to relocate');

    const destinations = joinLabels(locations?.map((loc) => loc.formattedLocationShort) ?? []);
    return destinations
        ? gt.gettext('Willing to relocate to: {0}', destinations)
        : gt.gettext('Willing to relocate');
};

/**
 * Creates a map from taxonomy concept key (suid) to label
 */
const createTaxonomyConceptMap = (
    taxonomyConcepts: Array<{ key: string; label: string }> | null | undefined,
): Map<string, string> => {
    const map = new Map<string, string>();
    if (taxonomyConcepts) {
        for (const concept of taxonomyConcepts) {
            map.set(concept.key, concept.label);
        }
    }
    return map;
};

/** Formats remote work preferences using taxonomy concept labels */
const formatRemotePreferences = (
    attributes: Array<{ suid: string; label: string; sentiment: string }> | null | undefined,
    remoteWorkTypeMap: Map<string, string>,
): string | null => {
    if (!attributes?.length) return null;
    const remoteLabels = attributes
        .filter((attr) => remoteWorkTypeMap.has(attr.suid) && attr.sentiment === 'POSITIVE')
        .map((attr) => remoteWorkTypeMap.get(attr.suid));
    return remoteLabels.length > 0 ? remoteLabels.join(', ') : null;
};

/**
 * MatchPreferencesSection Component
 * Displays job match preferences with an edit link
 *
 * This component is shared between:
 * - /auto-apply-setup/job-preferences page
 * - /auto-apply-setup/review page
 *
 * It uses cache-first fetch policy to reuse data across pages.
 * When the user navigates from job-preferences to review,
 * the data will be served from Apollo cache instead of making a new network request.
 *
 * Figma Design: https://www.figma.com/design/pXXAQfQFDZqsF1BxMo5ccB/Assisted-apply-2.0---Auto-Apply?node-id=5220-174981
 *
 * JIRA: IAFE-316 - Implement match preferences section in the /auto-apply-setup/review page
 * JIRA: IAFE-317 - Integrate Job match preferences GraphQL endpoint in the review step
 */
export interface MatchPreferencesSectionProps {
    /** Section heading. Defaults to "Match preferences". */
    title?: string;
    /** Optional icon rendered before the heading (e.g. the progressive-automation "Job preferences" variant). */
    icon?: React.ReactNode;
    editDisabled?: boolean;
}

export const MatchPreferencesSection: React.FC<MatchPreferencesSectionProps> = ({
    title,
    icon,
    editDisabled,
}) => {
    const { indeedProfileBaseUrl, locale, countryCode, origin } = useInitialState();
    const location = useLocation();
    const gt = GettextFactory();
    const eventLogger = useEventLogger();
    const [showErrorDialog, setShowErrorDialog] = useState(false);

    // Build preferences edit URL (redirects to Profile preferences page)
    const preferencesUrl = useMemo(() => {
        const url = new URL('/preferences', indeedProfileBaseUrl);
        url.searchParams.set('co', countryCode);
        url.searchParams.set('hl', locale);
        url.searchParams.set('from', 'auto-apply-preference');

        // Build continue URL so user returns to this page after editing preferences
        const continueUrl = new URL(`${origin}${location.pathname}${location.search}`);
        continueUrl.searchParams.set('from', 'profile-preferences-edit');
        url.searchParams.set('continue', continueUrl.toString());

        return url.toString();
    }, [indeedProfileBaseUrl, countryCode, locale, origin, location.pathname, location.search]);

    // Build input for remote work types taxonomy query
    // Extract language and country from locale (e.g., 'en-US' -> ['en', 'US'], 'en' -> ['en'])
    // Fall back to countryCode from InitialState if locale doesn't include country
    const [localeLanguageCode, localeCountryCode] = locale.split('-');
    const remoteWorkTypesInput: TaxonomyConceptsInput = useMemo(
        () => ({
            conceptIdFilter: [],
            customClassFilter: {
                customClasses: [REMOTE_WORK_TYPE_CUSTOM_CLASS],
            },
            countryCode: localeCountryCode || countryCode,
            languageCode: localeLanguageCode,
        }),
        [localeCountryCode, localeLanguageCode, countryCode],
    );

    // Fetch preferences and remote work type taxonomy concepts from GraphQL
    // This ensures data is reused when navigating between job-preferences and review pages
    const { data, loading, error, refetch } = useGetMatchPreferencesQuery({
        fetchPolicy: 'cache-first',
        variables: {
            remoteWorkTypesInput,
        },
    });

    // Show error dialog when query fails
    useEffect(() => {
        if (error) {
            logAutoApplySetupRumError(error, AutoApplySetupRumContext.MatchPreferencesLoad, false);
            setShowErrorDialog(true);
        }
    }, [error]);

    // Create a map from remote work type suid to translated label
    const remoteWorkTypeMap = useMemo(
        () => createTaxonomyConceptMap(data?.remoteWorkTypes?.taxonomyConcepts),
        [data?.remoteWorkTypes?.taxonomyConcepts],
    );

    // Extract preferences from query result (use first preference if available)
    const preferences = data?.jobSeekerProfileStructuredData?.preferences?.[0];

    // Format all fields - returns null if empty
    const NOT_ADDED = gt.gettext('Not added');
    const jobTitlesValue = formatJobTitles(preferences?.jobTitles) ?? NOT_ADDED;
    const workAreasValue = formatWorkAreas(preferences?.workAreas); // Hidden if empty (profile redesign)
    const jobTypesValue = formatJobTypes(preferences?.jobTypes) ?? NOT_ADDED;
    const shiftsValue = formatShifts(preferences?.shifts) ?? NOT_ADDED;
    const pay = preferences?.minimumPay;
    const minimumPayValue =
        formatMinimumPay(pay?.amount, pay?.currency, pay?.salaryType, locale, gt) ?? NOT_ADDED;
    const commuteValue = formatCommute(preferences?.maximumCommute?.timeMinutes, gt);
    const relocationValue =
        formatRelocation(preferences?.relocation?.ableToRelocate, preferences?.locations, gt) ??
        NOT_ADDED;
    const remoteValue =
        formatRemotePreferences(preferences?.attributes, remoteWorkTypeMap) ?? NOT_ADDED;

    const sectionTitle = title ?? gt.gettext('Match preferences');

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Section Header with Edit Link */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {icon && (
                        <Box data-testid="match-preferences-heading-icon" sx={{ flexShrink: 0 }}>
                            {icon}
                        </Box>
                    )}
                    <Heading level={3} as="h2" sx={{ marginBlockEnd: 0 }}>
                        {sectionTitle}
                    </Heading>
                </Box>
                <ButtonLink
                    href={editDisabled ? undefined : preferencesUrl}
                    role={editDisabled ? 'link' : undefined}
                    aria-disabled={editDisabled}
                    tabIndex={editDisabled ? -1 : undefined}
                    variant="tertiary"
                    aria-label={gt.gettext('Edit {0}', [sectionTitle])}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                    iconOnly
                    onClick={(event) => {
                        if (editDisabled) {
                            event.preventDefault();
                            return;
                        }
                        eventLogger.logAutoApplySetupButtonClickEvent?.({
                            currentPage: location.pathname,
                            button: 'edit-match-preferences',
                        });
                    }}
                >
                    <Edit size="md" variant="stroke" aria-hidden="true" />
                </ButtonLink>
            </Box>

            {/* Error Dialog */}
            <ErrorDialog
                isOpen={showErrorDialog}
                onOpenChange={setShowErrorDialog}
                onAction={() => refetch()}
            />

            {/* Preferences Card */}
            <Box
                sx={{
                    border: '1px solid',
                    borderColor: 'border.weak',
                    borderRadius: 'lg',
                    padding: 4,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 4,
                }}
            >
                {loading || error ? (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            padding: 4,
                        }}
                    >
                        {loading && <Spinner title={gt.gettext('Loading preferences')} />}
                    </Box>
                ) : (
                    <>
                        <JobPreferenceField
                            label={gt.gettext('Desired job titles')}
                            value={jobTitlesValue}
                            icon={<Profile size="sm" aria-hidden />}
                        />
                        {workAreasValue && (
                            <JobPreferenceField
                                label={gt.gettext('Work areas')}
                                value={workAreasValue}
                                icon={<OpenDoor size="sm" aria-hidden />}
                            />
                        )}
                        <JobPreferenceField
                            label={gt.gettext('Job types')}
                            value={jobTypesValue}
                            icon={<Jobs size="sm" aria-hidden />}
                        />
                        <JobPreferenceField
                            label={gt.gettext('Work schedules')}
                            value={shiftsValue}
                            icon={<Clock size="sm" aria-hidden />}
                        />
                        <JobPreferenceField
                            label={gt.gettext('Minimum base pay')}
                            value={minimumPayValue}
                            icon={<MoneyStack size="sm" aria-hidden />}
                        />
                        {commuteValue && (
                            <JobPreferenceField
                                label={gt.gettext('Commute time')}
                                value={commuteValue}
                                icon={<Commute size="sm" aria-hidden />}
                            />
                        )}
                        <JobPreferenceField
                            label={gt.gettext('Relocation')}
                            value={relocationValue}
                            icon={<Location size="sm" aria-hidden />}
                        />
                        <JobPreferenceField
                            label={gt.gettext('Remote')}
                            value={remoteValue}
                            icon={<Home size="sm" aria-hidden />}
                        />
                    </>
                )}
            </Box>
        </Box>
    );
};
